import { Helmet } from "react-helmet-async";

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: "website" | "article" | "course";
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
}

export const SEOHead = ({
  title = "LaunchToRevenue | Build, Automate & Scale Your Digital Business",
  description = "More than a course — LaunchToRevenue is your complete ecosystem for launching, automating, and growing online income with AI tools, a private community, and affiliate opportunities.",
  keywords = "digital business ecosystem, AI business tools, affiliate program, business dashboard, content planner, goal planner, business automation, private community, monetization tools, online business platform",
  image = "/hero-image.jpg",
  url = "https://launchtorevenue.com",
  type = "website",
  author = "LaunchToRevenue Team",
  publishedTime,
  modifiedTime,
}: SEOHeadProps) => {
  const fullTitle = title.includes("LaunchToRevenue") ? title : `${title} | LaunchToRevenue`;
  const fullImageUrl = image.startsWith('http') ? image : `${url}${image}`;
  
  // Structured Data - Organization
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "LaunchToRevenue",
    "url": url,
    "logo": `${url}/hero-image.jpg`,
    "description": description,
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "Customer Support",
      "email": "support@launch2revenue.com"
    },
    "sameAs": [
      "https://twitter.com/launch2revenue",
      "https://linkedin.com/company/launch2revenue",
      "https://instagram.com/launch2revenue"
    ]
  };

  // Structured Data - Course
  const courseSchema = type === "course" ? {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": title,
    "description": description,
    "provider": {
      "@type": "Organization",
      "name": "LaunchToRevenue",
      "url": url
    },
    "educationalLevel": "All Levels",
    "coursePrerequisites": "None",
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "Online",
      "courseWorkload": "PT12H"
    },
    "offers": {
      "@type": "Offer",
      "category": "Business & Entrepreneurship",
      "availability": "https://schema.org/InStock"
    }
  } : null;

  // Structured Data - Article (for blog posts)
  const articleSchema = type === "article" ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "image": fullImageUrl,
    "author": {
      "@type": "Person",
      "name": author
    },
    "publisher": {
      "@type": "Organization",
      "name": "LaunchToRevenue",
      "logo": {
        "@type": "ImageObject",
        "url": `${url}/hero-image.jpg`
      }
    },
    "datePublished": publishedTime,
    "dateModified": modifiedTime || publishedTime
  } : null;
  
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:site_name" content="LaunchToRevenue" />
      <meta property="og:locale" content="en_US" />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta name="twitter:image:alt" content={title} />
      <meta name="twitter:creator" content="@launch2revenue" />
      <meta name="twitter:site" content="@launch2revenue" />
      
      {/* Additional SEO */}
      <meta name="theme-color" content="#0EA5E9" />
      <meta name="format-detection" content="telephone=no" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={url} />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      {courseSchema && (
        <script type="application/ld+json">
          {JSON.stringify(courseSchema)}
        </script>
      )}
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}
    </Helmet>
  );
};
